/**
 * Match de endereço com cercas (Roteirizacao + processacsvroteirizacao).
 * Comparação/matching — não usar para exibição ou persistência do texto bruto.
 */

/**
 * @param {*} value string, Buffer (Node), ou { type: 'Buffer', data: number[] } (JSON)
 */
function decodeAddress(value) {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.type === 'Buffer' && Array.isArray(value.data)) {
        try {
            return new TextDecoder('utf-8').decode(new Uint8Array(value.data));
        } catch (_) {
            return String.fromCharCode.apply(null, value.data);
        }
    }
    if (typeof Buffer !== 'undefined' && Buffer.isBuffer(value)) {
        return value.toString('utf8');
    }
    return String(value);
}

/**
 * Regras de substituição de UF compiladas, memoizadas pela identidade do array
 * `fedStates` (atribuído uma única vez em `fetchStates`). Sem o cache,
 * `normalizeAddress` recompilaria 27 `RegExp` a cada chamada — o custo dominante
 * quando o CSV é casado contra milhares de cercas.
 */
const stateRulesCache = new WeakMap();

function getStateRules(fedStates) {
    if (!fedStates || typeof fedStates !== 'object') return [];
    const cached = stateRulesCache.get(fedStates);
    if (cached) return cached;
    const rules = (Array.isArray(fedStates) ? fedStates : [])
        .map((u) => {
            const longForm = String(u && u.descricao_uf != null ? u.descricao_uf : '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .trim()
                .replace(/\s+/g, ' ');
            const uf = String(u && u.codigo_uf != null ? u.codigo_uf : '')
                .toLowerCase()
                .trim();
            return { longForm, uf };
        })
        .filter((r) => r.longForm && r.uf && r.longForm !== r.uf)
        .sort((a, b) => b.longForm.split(' ').length - a.longForm.split(' ').length)
        .map(({ longForm, uf }) => {
            const escaped = longForm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return { re: new RegExp(`(,\\s*)${escaped}(?=\\s*(?:,|$|\\d))`, 'g'), uf };
        });
    stateRulesCache.set(fedStates, rules);
    return rules;
}

/**
 * @param {Array<{ descricao_uf?: string, codigo_uf?: string }>} fedStates
 */
function normalizeAddress(str, fedStates) {
    if (str == null || typeof str !== 'string') return '';
    let s = str.trim();
    if (!s) return '';
    s = s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    s = s.replace(/\bbrazil\b/g, 'brasil');
    s = s.replace(/(\d)\s*-\s*(\d)/g, '$1$2');
    s = s.replace(/\s+-\s+/g, ', ');
    const estadoRules = getStateRules(fedStates);
    for (const { re, uf } of estadoRules) {
        // `re` é global e reusado entre chamadas: `replace` já reseta lastIndex,
        // mas manter a mesma instância exige não usar `test`/`exec` aqui.
        s = s.replace(re, (_m, before) => `${before}${uf}`);
    }
    s = s.replace(/[.,;:_\-–—/\\]/g, ' ');
    s = s.replace(/\s+/g, ' ').trim();
    if (!s) return '';
    const tokens = s.split(' ');
    const dedup = [];
    for (const t of tokens) {
        if (!t) continue;
        if (dedup[dedup.length - 1] !== t) dedup.push(t);
    }
    return dedup.join(' ');
}

function getFenceAddress(cerca) {
    if (!cerca) return '';
    const raw = cerca.endereco;
    if (raw == null) return '';
    if (typeof raw === 'string') return raw;
    return decodeAddress(raw) || '';
}

/**
 * Índice endereço normalizado -> cerca, para substituir a varredura linear de
 * `findFenceByAddress`. Mantém a semântica *first-wins* da varredura: quando
 * duas cercas normalizam para o mesmo endereço (getCercasSelect duplica linhas
 * por causa do LEFT JOIN sem GROUP BY), vence a primeira do array.
 *
 * @returns {Map<string, object>}
 */
function buildFenceAddressIndex(fences, fedStates) {
    const map = new Map();
    if (!fences || !fences.length) return map;
    for (let i = 0; i < fences.length; i++) {
        const c = fences[i];
        const key = normalizeAddress(getFenceAddress(c), fedStates);
        if (!key) continue;
        if (!map.has(key)) map.set(key, c);
    }
    return map;
}

/**
 * @param {Map<string, object>} [index] índice de `buildFenceAddressIndex` para a
 *   mesma dupla (fences, fedStates). Ausente, cai na varredura linear.
 */
function findFenceByAddress(endereco, fences, fedStates, index) {
    if (!endereco) return null;
    if (!index && (!fences || !fences.length)) return null;
    const normalized = normalizeAddress(String(endereco), fedStates);
    if (!normalized) return null;
    if (index) return index.get(normalized) || null;
    for (let i = 0; i < fences.length; i++) {
        const c = fences[i];
        if (normalizeAddress(getFenceAddress(c), fedStates) === normalized) return c;
    }
    return null;
}

module.exports = {
    decodeAddress,
    normalizeAddress,
    getFenceAddress,
    buildFenceAddressIndex,
    findFenceByAddress,
};
